import customers from '@/services/customers'
import { buildCtiCommand, buildInitiateCallPayload, toNationalDigits } from '@/utils/dialpadCti'

// State for the embedded Dialpad Mini Dialer (DialpadMiniDialer.vue). Commands are queued here and
// the component posts them to the iframe, so callers anywhere in the app don't need the frame.

export const namespaced = true

const SCREEN_POP_LIMIT = 5

export const state = {
  // True while DialpadMiniDialer is mounted, so click-to-dial knows to use it instead of Twilio
  isEnabled: false,
  isPanelOpen: false,
  isAuthenticated: false,
  dialpadUserId: null,
  ringingCall: null,
  // The last inbound caller and their CoachRail matches. Outlives the ringing so the agent can still
  // open the record once they've answered.
  screenPop: null,
  // A click-to-dial call waiting on the agent's confirmation: { request, name }
  pendingCall: null,
  commandQueue: [],
}

export const mutations = {
  SET_ENABLED(state, isEnabled) {
    state.isEnabled = isEnabled
  },
  SET_PANEL_OPEN(state, isOpen) {
    state.isPanelOpen = isOpen
  },
  SET_AUTHENTICATION(state, { isAuthenticated, dialpadUserId }) {
    state.isAuthenticated = isAuthenticated
    state.dialpadUserId = dialpadUserId
  },
  SET_RINGING_CALL(state, call) {
    state.ringingCall = call
  },
  SET_SCREEN_POP(state, screenPop) {
    state.screenPop = screenPop
  },
  SET_PENDING_CALL(state, pendingCall) {
    state.pendingCall = pendingCall
  },
  QUEUE_COMMAND(state, command) {
    state.commandQueue.push(command)
  },
  CLEAR_COMMAND_QUEUE(state) {
    state.commandQueue = []
  },
}

export const getters = {
  isEnabled: (s) => s.isEnabled,
  isPanelOpen: (s) => s.isPanelOpen,
  isAuthenticated: (s) => s.isAuthenticated,
  dialpadUserId: (s) => s.dialpadUserId,
  ringingCall: (s) => s.ringingCall,
  screenPop: (s) => s.screenPop,
  pendingCall: (s) => s.pendingCall,
  commandQueue: (s) => s.commandQueue,
}

export const actions = {
  setEnabled({ commit }, isEnabled) {
    commit('SET_ENABLED', isEnabled)
  },
  setPanelOpen({ commit }, isOpen) {
    commit('SET_PANEL_OPEN', isOpen)
  },
  togglePanel({ commit, state }) {
    commit('SET_PANEL_OPEN', !state.isPanelOpen)
  },
  handleUserAuthentication({ commit }, payload) {
    commit('SET_AUTHENTICATION', {
      isAuthenticated: !!payload?.user_authenticated,
      dialpadUserId: payload?.user_id ?? null,
    })
  },
  handleCallRinging({ commit, dispatch, state }, payload) {
    if (payload?.state === 'on') {
      commit('SET_RINGING_CALL', payload)
      // The agent answers inside the iframe, so it has to be visible
      commit('SET_PANEL_OPEN', true)
      // Every open tab gets call_ringing, so each one looks the caller up
      return dispatch('lookUpCaller', payload)
    } else if (payload?.state === 'off' && state.ringingCall?.id === payload.id) {
      commit('SET_RINGING_CALL', null)
    }
  },
  async lookUpCaller({ commit, state }, call) {
    const base = {
      callId: call.id,
      // contact.phone is display-formatted, so search on the E.164 external_number
      phone: call.external_number,
      name: call.contact?.name || null,
    }
    const phone = toNationalDigits(call.external_number)
    if (!phone) {
      commit('SET_SCREEN_POP', { ...base, status: 'done', matches: [] })
      return
    }
    commit('SET_SCREEN_POP', { ...base, status: 'loading', matches: [] })
    try {
      const { data } = await customers.searchCustomers({ phone, limit: SCREEN_POP_LIMIT })
      // A newer call may have rung while this one was in flight
      if (state.screenPop?.callId !== call.id) return
      commit('SET_SCREEN_POP', { ...base, status: 'done', matches: data?.results || [] })
    } catch (e) {
      console.warn('[dialpad:cti] caller lookup failed', e)
      if (state.screenPop?.callId !== call.id) return
      commit('SET_SCREEN_POP', { ...base, status: 'error', matches: [] })
    }
  },
  dismissScreenPop({ commit }) {
    commit('SET_SCREEN_POP', null)
  },
  enableCurrentTab({ commit }) {
    commit('QUEUE_COMMAND', buildCtiCommand('enable_current_tab'))
  },
  hangUpAllCalls({ commit }) {
    commit('QUEUE_COMMAND', buildCtiCommand('hang_up_all_calls'))
  },
  /**
   * Places an outbound call through the CTI. Takes an InitiateCallRequest
   * ({ phoneNumber, identityType, identityId, customData, outboundCallerId }) and throws if it's invalid.
   */
  async initiateCall({ commit }, request) {
    const payload = buildInitiateCallPayload(request)
    commit('QUEUE_COMMAND', buildCtiCommand('initiate_call', payload))
    commit('SET_PANEL_OPEN', true)
  },
  /**
   * Click-to-dial from a CoachRail record. Asks the agent to confirm first, as the Twilio Flex dialer
   * does, and tags the call with the record's IDs in custom_data, which Dialpad returns on the call as
   * {"open_cti": "<this JSON>"}.
   */
  callFromRecord({ commit, dispatch }, { phoneNumber, name = null, quoteId = null, reservationId = null, customerId = null }) {
    const request = {
      phoneNumber,
      customData: JSON.stringify({ source: 'coachrail', quoteId, reservationId, customerId }),
    }
    try {
      // Validate now so a bad number fails on click, not after the agent confirms
      buildInitiateCallPayload(request)
    } catch (e) {
      dispatch('app/showAlert', { type: 'error', message: e.message }, { root: true })
      return
    }
    commit('SET_PENDING_CALL', { request, name })
  },
  async confirmPendingCall({ commit, dispatch, state }) {
    const pendingCall = state.pendingCall
    commit('SET_PENDING_CALL', null)
    if (pendingCall) {
      await dispatch('initiateCall', pendingCall.request)
    }
  },
  cancelPendingCall({ commit }) {
    commit('SET_PENDING_CALL', null)
  },
  takeCommands({ commit, state }) {
    const commands = state.commandQueue
    commit('CLEAR_COMMAND_QUEUE')
    return commands
  },
}
